"use client";

import { type ReactElement, type ReactNode, useId, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";

type DisplaySettingsSurfaceProps = {
  trigger: ReactElement;
  triggerTooltip?: ReactNode;
  accessibleTitle: string;
  mobileTitle: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
};

function isNestedSelectTarget(target: EventTarget | null) {
  return (
    target instanceof Element &&
    target.closest("[data-display-settings-select-content]") !== null
  );
}

/**
 * Display settings を desktop の Popover、mobile の全画面 Sheet として表示します。
 * open state は内部で制御し、breakpoint が変わった場合は異なる Radix surface 間で
 * focus state を持ち越さないよう閉じます。nested Select との interaction は外側を閉じず、
 * `accessibleTitle` は desktop dialog と非表示 heading の名前として必須です。
 */
function DisplaySettingsSurface({
  trigger,
  triggerTooltip,
  accessibleTitle,
  mobileTitle,
  children,
  footer,
}: DisplaySettingsSurfaceProps) {
  const isMobile = useIsMobile();
  const [surfaceState, setSurfaceState] = useState({ isMobile, open: false });
  if (surfaceState.isMobile !== isMobile) {
    setSurfaceState({ isMobile, open: false });
  }
  const open = surfaceState.isMobile === isMobile && surfaceState.open;
  const setOpen = (nextOpen: boolean) => {
    setSurfaceState({ isMobile, open: nextOpen });
  };

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <SheetTrigger asChild>{trigger}</SheetTrigger>
          </TooltipTrigger>
          {triggerTooltip ? (
            <TooltipContent className="md:hidden">
              {triggerTooltip}
            </TooltipContent>
          ) : null}
        </Tooltip>
        <SheetContent
          side="right"
          className="h-dvh w-full max-w-none gap-0 overflow-hidden p-0 sm:max-w-none"
          onInteractOutside={(event) => {
            if (isNestedSelectTarget(event.target)) event.preventDefault();
          }}
        >
          <SheetHeader className="shrink-0 border-b px-4 py-3 pr-12">
            <SheetTitle className="text-base leading-6">
              {mobileTitle}
            </SheetTitle>
          </SheetHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
            {children}
          </div>
          {footer}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        </TooltipTrigger>
        {triggerTooltip ? (
          <TooltipContent className="md:hidden">
            {triggerTooltip}
          </TooltipContent>
        ) : null}
      </Tooltip>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={6}
        avoidCollisions={false}
        aria-label={accessibleTitle}
        className="flex h-[var(--radix-popover-content-available-height)] w-[23rem] flex-col overflow-hidden p-0"
        onInteractOutside={(event) => {
          if (isNestedSelectTarget(event.target)) event.preventDefault();
        }}
      >
        <h2 className="sr-only">{accessibleTitle}</h2>
        <ScrollArea className="min-h-0 flex-1">{children}</ScrollArea>
        {footer}
      </PopoverContent>
    </Popover>
  );
}

type DisplaySettingsTriggerProps = React.ComponentProps<typeof Button>;

/**
 * Display settings を開く標準の shadcn Button trigger です。
 * 呼び出し側の aria-label と event handler を Button へ透過し、既定の寸法だけを提供します。
 */
function DisplaySettingsTrigger({
  children,
  className,
  variant = "outline",
  size = "sm",
  ...props
}: DisplaySettingsTriggerProps) {
  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={cn("h-8 gap-1.5 px-2", className)}
      {...props}
    >
      {children}
    </Button>
  );
}

type DisplaySettingsSectionProps = React.ComponentProps<"section"> & {
  heading: ReactNode;
  disabled?: boolean;
  hint?: ReactNode;
};

/**
 * 見出しと任意の補足説明で設定項目を accessible group としてまとめます。
 * disabled は子 control を無効化しないため、呼び出し側も各 control へ伝播させます。
 */
function DisplaySettingsSection({
  heading,
  disabled = false,
  hint,
  className,
  children,
  ...props
}: DisplaySettingsSectionProps) {
  const titleId = useId();
  const hintId = useId();

  return (
    <section
      role="group"
      aria-labelledby={titleId}
      aria-describedby={hint ? hintId : undefined}
      aria-disabled={disabled || undefined}
      className={cn("px-2 py-2.5", disabled && "opacity-60", className)}
      {...props}
    >
      <div className="px-1 pb-1.5">
        <h3
          id={titleId}
          className="text-xs font-medium leading-5 text-muted-foreground"
        >
          {heading}
        </h3>
        {hint ? (
          <DisplaySettingsSectionHint id={hintId}>
            {hint}
          </DisplaySettingsSectionHint>
        ) : null}
      </div>
      <div className="flex flex-col gap-0.5">{children}</div>
    </section>
  );
}

/** セクション見出しの `aria-describedby` に紐づけられる補足説明です。 */
function DisplaySettingsSectionHint({
  className,
  ...props
}: React.ComponentProps<"p">) {
  return (
    <p
      className={cn("mt-1 text-xs leading-5 text-muted-foreground", className)}
      {...props}
    />
  );
}

/** ラベルと controlled input を配置するための、状態を持たない標準設定行です。 */
function DisplaySettingsRow({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "min-h-8 rounded-md px-1 py-1 text-sm leading-5 transition-colors",
        className,
      )}
      {...props}
    />
  );
}

/** surface の scroll 領域外に置かれ、下端に残る action 領域です。 */
function DisplaySettingsFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-end gap-2 border-t px-3 py-2.5",
        className,
      )}
      {...props}
    />
  );
}

/** 隣接する設定セクション間に意味を持たない視覚的な境界を表示します。 */
function DisplaySettingsSeparator() {
  return <Separator />;
}

export {
  DisplaySettingsFooter,
  DisplaySettingsRow,
  DisplaySettingsSection,
  DisplaySettingsSeparator,
  DisplaySettingsSurface,
  DisplaySettingsTrigger,
};
